import type { Asset, MatNode } from '../store/seedanceTypes';
import { getVirtualPortrait } from '../api/virtualPortrait';
import type { AssetPreviewSource } from './seedanceLibraryAssetsSnapshot';
import {
  getLibraryAssetPreviewKey,
  loadLibraryAssetPreviewCache,
  persistLibraryAssetPreviewCache,
} from './seedanceLibraryAssetsSnapshot';
import {
  pickPortraitMainUrl,
  pickPortraitPlayableUrl,
  pickPortraitThumbUrl,
  resolvePortraitImageUrl,
  unwrapPortraitListItem,
} from './seedancePortraitUtils';
import { SEEDANCE_STORAGE_KEYS, getLocalStorageValue } from './seedanceStorage';
import { useSeedanceWorkspaceStore } from '../store/seedanceWorkspaceStore';

export interface RefreshMatPortraitsOptions {
  onDebug?: (message: string, isError?: boolean) => void;
  signal?: AbortSignal;
}

export type MatPortraitPatch = {
  sourceAssetId: string;
  url: string;
  thumb: string;
};

/** 只从同类型缓存素材回填预览，避免把音频地址写进图片/视频槽。 */
export function buildCachedMatPortraitPatches(
  nodes: MatNode[],
  assets: Asset[],
): Map<string, MatPortraitPatch> {
  const assetBySourceId = new Map<string, Asset>();
  assets.forEach((asset) => {
    const sid = (asset.sourceAssetId || asset.id || '').trim();
    if (sid) assetBySourceId.set(getLibraryAssetPreviewKey(asset.type, sid), asset);
  });

  const out = new Map<string, MatPortraitPatch>();
  nodes.forEach((node) => {
    const sid = (node.sourceAssetId || '').trim();
    if (!sid) return;
    const asset = assetBySourceId.get(getLibraryAssetPreviewKey(node.type, sid));
    if (!asset) return;
    const url = (asset.url || '').trim();
    const thumb = (asset.thumb || url).trim();
    if (!url && !thumb) return;
    out.set(node.id, { sourceAssetId: sid, url, thumb });
  });
  return out;
}

/** 从 IndexedDB 预览字典读取，失败时静默回退网络详情。 */
export async function loadCachedMatPortraitPatches(
  nodes: MatNode[],
  options: Pick<RefreshMatPortraitsOptions, 'onDebug'> = {},
): Promise<Map<string, MatPortraitPatch>> {
  try {
    const previews = await loadLibraryAssetPreviewCache();
    const patches = buildCachedMatPortraitPatches(nodes, previews);
    if (patches.size) {
      options.onDebug?.(`工作台素材预览：命中共享缓存 ${patches.size} 个素材，先显示缓存预览`);
    }
    return patches;
  } catch {
    return new Map();
  }
}

/** 素材库卡片无预览时，复用工作台已有同 type+id 节点的地址。 */
export function resolveAssetForWorkspace(asset: Asset, workspaceNodes: MatNode[]): Asset {
  const url = (asset.url || '').trim();
  const thumb = (asset.thumb || '').trim();
  if (url || thumb) return asset;

  const sourceId = (asset.sourceAssetId || asset.id || '').trim();
  if (!sourceId) return asset;

  const matchingNode = workspaceNodes.find(
    (node) =>
      node.type === asset.type &&
      (node.sourceAssetId || '').trim() === sourceId &&
      ((node.url || '').trim() || (node.thumb || '').trim()),
  );
  if (!matchingNode) return asset;

  return {
    ...asset,
    url: matchingNode.url || null,
    thumb: matchingNode.thumb || matchingNode.url || null,
  };
}

/** GetAsset / enrich 成功后，回填工作台里同 type+id 且 url 为空的节点。 */
export function backfillWorkspaceNodesFromPreviewSources(sources: AssetPreviewSource[]): void {
  const patches = new Map<string, { url: string; thumb: string }>();
  sources.forEach((source) => {
    const sid = (source.sourceAssetId || source.id || '').trim();
    if (!sid) return;
    const url = (source.url || '').trim();
    const thumb = (source.thumb || url).trim();
    if (!url && !thumb) return;
    const key = getLibraryAssetPreviewKey(source.type, sid);
    const previous = patches.get(key);
    patches.set(key, {
      url: url || previous?.url || '',
      thumb: thumb || previous?.thumb || '',
    });
  });
  if (!patches.size) return;

  const { setNodes } = useSeedanceWorkspaceStore.getState();
  setNodes((prev) =>
    prev.map((node) => {
      const sid = (node.sourceAssetId || '').trim();
      if (!sid || (node.url || '').trim()) return node;
      const patch = patches.get(getLibraryAssetPreviewKey(node.type, sid));
      if (!patch) return node;
      return {
        ...node,
        ...(patch.url ? { url: patch.url } : {}),
        ...(patch.thumb ? { thumb: patch.thumb } : {}),
      };
    }),
  );
}

/**
 * 工作台素材槽：未命中本地快照时，通过 `GET /api/virtual_portrait/:id`
 * 按 sourceAssetId 拉取最新 URL 展示。
 */
export async function refreshMatNodesPortraitUrls(
  nodes: MatNode[],
  options: RefreshMatPortraitsOptions = {},
): Promise<Map<string, MatPortraitPatch>> {
  const { onDebug, signal } = options;
  const out = new Map<string, { sourceAssetId: string; url: string; thumb: string }>();

  const token = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.apiKey).trim();
  const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
  if (!token || !userId) {
    onDebug?.('工作台素材预览：未配置系统令牌与用户 ID，无法拉取素材详情', true);
    return out;
  }

  const targets = nodes.filter((n) => {
    const sid = (n.sourceAssetId || '').trim();
    if (!sid || (n.type !== 'image' && n.type !== 'video' && n.type !== 'audio')) return false;
    return true;
  });
  if (!targets.length) return out;

  onDebug?.(`工作台素材预览：按素材 ID 拉取详情（${targets.length} 个）…`);

  const targetsByPreviewKey = new Map<string, MatNode[]>();
  targets.forEach((node) => {
    const sid = (node.sourceAssetId || '').trim();
    const key = getLibraryAssetPreviewKey(node.type, sid);
    const group = targetsByPreviewKey.get(key) || [];
    group.push(node);
    targetsByPreviewKey.set(key, group);
  });

  await Promise.all(
    Array.from(targetsByPreviewKey.entries()).map(async ([, groupedNodes]) => {
      const sid = (groupedNodes[0]?.sourceAssetId || '').trim();
      try {
        const data = await getVirtualPortrait(sid, {
          onDebug,
          signal,
          systemToken: token,
          userId,
        });
        if (signal?.aborted) return;
        const nodeType = groupedNodes[0]?.type || 'image';
        const mainUrl = pickPortraitPlayableUrl(
          unwrapPortraitListItem(data),
          nodeType === 'video' ? 'video' : nodeType === 'audio' ? 'audio' : 'image',
        ) || pickPortraitMainUrl(data);
        const url = resolvePortraitImageUrl(mainUrl);
        if (!url) {
          onDebug?.(`工作台素材预览：素材 ${sid} 详情无可用 URL`, true);
          return;
        }
        const thumbRaw = pickPortraitThumbUrl(data, mainUrl);
        const thumb = resolvePortraitImageUrl(thumbRaw) || url;
        groupedNodes.forEach((node) => {
          out.set(node.id, { sourceAssetId: sid, url, thumb });
        });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        onDebug?.(`工作台素材预览：拉取 ${sid} 失败 — ${msg}`, true);
      }
    }),
  );

  if (out.size) {
    const enriched = targets.flatMap((node) => {
      const patch = out.get(node.id);
      return patch ? [{ ...node, url: patch.url, thumb: patch.thumb }] : [];
    });
    await persistLibraryAssetPreviewCache(enriched);
  }

  return out;
}
